import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";

const API = process.env.REACT_APP_API_URL || "https://doctor-appointment-1-yf2j.onrender.com";

/* ─── Particle System Component ─────────────────────────────────────────── */
const FloatingParticles = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createParticles = () => {
      const particleCount = 100;
      particles.current = [];
      for (let i = 0; i < particleCount; i++) {
        particles.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * (i % 5 === 0 ? 12 : 4) + 1,
          speedX: (Math.random() - 0.5) * 0.7,
          speedY: (Math.random() - 0.5) * 0.7,
          color: i % 5 === 0 ? "rgba(99, 179, 237, 0.3)" : "rgba(190, 227, 248, 0.45)",
          type: i % 10 === 0 ? "flower" : "dot"
        });
      }
    };

    const handleMouseMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    };

    const drawFlowerSymbol = (ctx, x, y, size, color) => {
      ctx.beginPath();
      ctx.fillStyle = color;
      for (let i = 0; i < 5; i++) {
        const angle = (i * 2 * Math.PI) / 5;
        const px = x + Math.cos(angle) * (size / 2);
        const py = y + Math.sin(angle) * (size / 2);
        ctx.moveTo(px, py);
        ctx.arc(px, py, size / 3, 0, Math.PI * 2);
      }
      ctx.fill();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.current.forEach((p) => {
        p.x += p.speedX; p.y += p.speedY;
        if (p.x < 0 || p.x > canvas.width) p.speedX *= -1;
        if (p.y < 0 || p.y > canvas.height) p.speedY *= -1;
        const dx = p.x - mouse.current.x;
        const dy = p.y - mouse.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 150) { p.x += dx * 0.02; p.y += dy * 0.02; }
        if (p.type === "flower") drawFlowerSymbol(ctx, p.x, p.y, p.size, p.color);
        else {
          ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color; ctx.fill();
        }
      });
      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);
    handleResize(); createParticles(); animate();
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }} />;
};

/* ─── STYLES ─────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

  .apt-root {
    min-height: 100vh;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    font-family: 'Outfit', sans-serif;
    color: #1e293b;
    position: relative;
    padding: 40px 24px 80px;
  }

  .apt-container {
    max-width: 1000px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  .apt-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
  }

  .apt-title {
    font-size: 32px;
    font-weight: 800;
    color: #0c4a6e;
    margin: 0;
  }

  .apt-section-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 40px 0 20px;
  }

  .apt-section-label {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #64748b;
  }

  .apt-section-badge {
    background: #e0f2fe;
    color: #0369a1;
    font-size: 12px;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 50px;
  }

  .apt-section-line {
    flex: 1;
    height: 1px;
    background: #cbd5e1;
  }

  .apt-card {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 20px;
    padding: 20px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    box-shadow: 0 10px 25px rgba(186, 230, 253, 0.2);
    transition: all 0.3s ease;
  }

  .apt-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(186, 230, 253, 0.4);
    background: rgba(255, 255, 255, 0.95);
  }

  .apt-card-info { flex: 1; }
  .apt-dr-name { font-size: 18px; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
  .apt-details { font-size: 14px; color: #475569; display: flex; flex-direction: column; gap: 2px; }
  
  .apt-badge {
    display: inline-block;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 12px;
    border-radius: 50px;
    text-transform: uppercase;
    margin-top: 10px;
  }

  .apt-badge-upcoming { background: #dcfce7; color: #16a34a; border: 1px solid #bbf7d0; }
  .apt-badge-completed { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; }
  .apt-badge-cancelled { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }

  .apt-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-end;
  }

  .apt-btn {
    padding: 8px 20px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    border: none;
    text-decoration: none;
    text-align: center;
  }

  .apt-btn-join { background: linear-gradient(135deg, #0284c7, #0ea5e9); color: #fff; box-shadow: 0 4px 12px rgba(2, 132, 199, 0.2); }
  .apt-btn-reschedule { background: #fff; color: #0c4a6e; border: 1px solid #e0f2fe; }
  .apt-btn-cancel { background: #fee2e2; color: #b91c1c; }

  .apt-btn:hover { transform: scale(1.05); }

  /* Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.4);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
  }

  .modal-card {
    background: #fff;
    width: 400px;
    max-width: 90%;
    border-radius: 24px;
    padding: 32px;
    box-shadow: 0 30px 60px rgba(15, 23, 42, 0.3);
  }

  .modal-title { font-size: 20px; font-weight: 800; color: #0c4a6e; margin-bottom: 24px; }
  .modal-label { display: block; font-size: 13px; font-weight: 700; color: #475569; margin-bottom: 6px; }
  .modal-input {
    width: 100%;
    padding: 12px 16px;
    border-radius: 12px;
    border: 2px solid #f1f5f9;
    background: #f8fafc;
    font-size: 14px;
    font-family: inherit;
    margin-bottom: 16px;
    outline: none;
    transition: all 0.3s ease;
  }
  .modal-input:focus { border-color: #0284c7; background: #fff; box-shadow: 0 0 0 4px rgba(2, 132, 199, 0.1); }

  .modal-footer { display: flex; gap: 12px; margin-top: 12px; }
  .modal-btn { flex: 1; padding: 12px; border-radius: 14px; font-weight: 700; cursor: pointer; transition: all 0.3s ease; border: none; }
  .modal-btn-save { background: linear-gradient(135deg, #0284c7, #0ea5e9); color: #fff; }
  .modal-btn-cancel { background: #f1f5f9; color: #64748b; }
`;

const statusColors = {
  upcoming: { border: "#378ADD", badgeClass: "apt-badge-upcoming" },
  completed: { border: "#639922", badgeClass: "apt-badge-completed" },
  cancelled: { border: "#888780", badgeClass: "apt-badge-cancelled" },
};

const Badge = ({ label, type }) => {
  const badgeClass = statusColors[type]?.badgeClass || "apt-badge-upcoming";
  return <span className={`apt-badge ${badgeClass}`}>{label}</span>;
};

const SectionHeader = ({ title, count }) => (
  <div className="apt-section-header">
    <span className="apt-section-label">{title}</span>
    {count > 0 && <span className="apt-section-badge">{count}</span>}
    <div className="apt-section-line" />
  </div>
);

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [newDuration, setNewDuration] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [newTime, setNewTime] = useState("");
  const [cancelId, setCancelId] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);


  const styleInjected = useRef(false);

  useEffect(() => {
    if (!styleInjected.current) {
      const tag = document.createElement("style");
      tag.textContent = STYLES;
      document.head.appendChild(tag);
      styleInjected.current = true;
    }
  }, []);

  let storedUser = null;
  try {
    const raw = localStorage.getItem("user");
    storedUser = raw && raw !== "undefined" ? JSON.parse(raw) : null;
  } catch {
    storedUser = null;
  }

  const userId = storedUser?.id || storedUser?.userId;

  const fetchUserProfile = async () => {
    try {
      if (!userId) return;
      await axios.get(`${API}/api/users/profile/${userId}`);
    } catch (err) { }
  };

  const fetchAppointments = async () => {
    try {
      if (!userId) return;
      const res = await axios.get(`${API}/api/appointments/user/${userId}`);
      setAppointments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch appointments:", err);
      toast.error(err.response?.data?.message || "Failed to fetch appointments");
    }
  };

  useEffect(() => {
    if (userId) {
      fetchAppointments();
      fetchUserProfile();
    }
  }, [userId]);


  const formatDateTime = (date, time) => {
    if (!date || !time) return "Invalid date";
    try {
      const d = new Date(date);
      const parts = time.split(":");
      d.setHours(Number(parts[0]));
      d.setMinutes(Number(parts[1]));
      d.setSeconds(0);
      return d.toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return "Invalid date"; }
  };

  // Derive section type for each appointment
  const getSectionType = (appt) => {
    if (appt.status === "cancelled") return "cancelled";
    if (appt.status === "completed") return "completed";

    const now = new Date();

    const apptTime = getApptDateTime(appt); // ✅ use your existing function

    return apptTime >= now ? "upcoming" : "completed";
  };

  // Group and sort appointments
  const now = new Date();

  const getApptDateTime = (appt) => {
    const d = new Date(appt.date);
    if (appt.time) {
      const [h, m] = appt.time.split(":").map(Number);
      d.setHours(h, m, 0, 0);
    }
    return d;
  };

  const upcoming = appointments
    .filter((a) => a.status !== "cancelled" && a.status !== "completed"
      && getApptDateTime(a) >= now)
    .sort((a, b) => getApptDateTime(a) - getApptDateTime(b));

  const completed = appointments
    .filter((a) => a.status === "completed" ||
      (a.status !== "cancelled" && getApptDateTime(a) < now))
    .sort((a, b) => getApptDateTime(b) - getApptDateTime(a));

  const cancelled = appointments
    .filter((a) => a.status === "cancelled")
    .sort((a, b) => getApptDateTime(b) - getApptDateTime(a));

  useEffect(() => {
    const fetchSlots = async () => {
      if (!newDate || !newDuration || !rescheduleId) return;
      try {
        const appt = appointments.find((a) => a.id === rescheduleId);
        if (!appt) return;
        const doctorId = appt.doctor_id;
        const res = await axios.get(
          `${API}/api/appointments/doctor/${doctorId}?date=${newDate}`
        );
        const booked = Array.isArray(res.data) ? res.data.map((a) => a.time) : [];
        const doctorRes = await axios.get(`${API}/api/doctors/${doctorId}`);
        const doctor = doctorRes.data;
        const [startH, startM] = doctor.availability_start.split(":").map(Number);
        const [endH, endM] = doctor.availability_end.split(":").map(Number);
        let startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        const slotMinutes = parseInt(newDuration, 10);
        const slots = [];
        while (startMinutes + slotMinutes <= endMinutes) {
          const h = Math.floor(startMinutes / 60);
          const m = startMinutes % 60;
          const slotStart = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
          const slotEndMinutes = startMinutes + slotMinutes;
          const slotEndH = Math.floor(slotEndMinutes / 60);
          const slotEndM = slotEndMinutes % 60;
          const slotEnd = `${slotEndH.toString().padStart(2, "0")}:${slotEndM.toString().padStart(2, "0")}`;
          const slotLabel = `${slotStart} → ${slotEnd}`;
          if (!booked.includes(slotStart)) slots.push({ value: slotStart, label: slotLabel });
          startMinutes += slotMinutes;
        }
        setAvailableSlots(slots);
      } catch (err) { toast.error("Failed to fetch available slots"); }
    };
    fetchSlots();
  }, [newDate, newDuration, rescheduleId, appointments]);

  const openRescheduleModal = (appt) => {
    setRescheduleId(appt.id);
    setNewDate(appt.date.split("T")[0]);
    setNewDate(appt.date.split("T")[0]);
    setNewTime(appt.time);
    setNewDuration(appt.duration || 30);
  };
  const openCancelModal = (id) => {
    setCancelId(id);
  };

  // 3️⃣ Function to confirm cancellation
  const confirmCancelAppointment = async () => {
    if (isCancelling) return; // prevent multiple clicks

    setIsCancelling(true);

    try {
      await axios.put(`${API}/api/appointments/cancel/${cancelId}`);
      toast.success("Appointment cancelled");
      setCancelId(null);
      fetchAppointments();
    } catch (err) {
      toast.error("Failed to cancel appointment");
    } finally {
      setIsCancelling(false);
    }
  };

  const rescheduleAppointment = async () => {
    if (!newDate || !newTime || !newDuration)
      return toast.error("Select new date, time, and duration");
    try {
      await axios.put(`${API}/api/appointments/reschedule/${rescheduleId}`, {
        date: newDate, time: newTime, duration: newDuration,
      });
      toast.success("Appointment rescheduled");
      setRescheduleId(null);
      setAvailableSlots([]);
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reschedule");
    }
  };

  const AppointmentCard = ({ appt }) => {
    const type = getSectionType(appt);
    const borderColor = statusColors[type]?.border || "#ccc";

    return (
      <div className="apt-card">
        <div className="apt-card-info" style={{ borderLeft: `4px solid ${borderColor}`, paddingLeft: '16px' }}>
          <p className="apt-dr-name">{appt.doctor_name}</p>
          <div className="apt-details">
            <span>📅 {formatDateTime(appt.date, appt.time)}</span>
            <span>📍 {appt.appointment_type} · {appt.duration} mins</span>
            {appt.meeting_link && type === "upcoming" && (
              <a href={appt.meeting_link} target="_blank" rel="noreferrer" style={{ color: '#0369a1', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                Join meeting ↗
              </a>
            )}
          </div>
          <Badge label={type.charAt(0).toUpperCase() + type.slice(1)} type={type} />
        </div>

        <div className="apt-actions">
          {type === "cancelled" ? (
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8' }}>CANCELLED</span>
          ) : type === "completed" ? (
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#0369a1' }}>COMPLETED</span>
          ) : (
            <>
              {appt.meeting_link && (
                <a href={appt.meeting_link} target="_blank" rel="noreferrer" className="apt-btn apt-btn-join">Join</a>
              )}
              <button onClick={() => openRescheduleModal(appt)} className="apt-btn apt-btn-reschedule">Reschedule</button>
              <button onClick={() => openCancelModal(appt.id)} className="apt-btn apt-btn-cancel">Cancel</button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="apt-root">
      <FloatingParticles />
      <div className="apt-container">
        <div className="apt-header">
          <h1 className="apt-title">My Appointments</h1>
        </div>

        {appointments.length === 0 && (
          <div className="apt-card" style={{ justifyContent: 'center', color: '#94a3b8' }}>
            <p>You have no appointments yet.</p>
          </div>
        )}

        {upcoming.length > 0 && (
          <>
            <SectionHeader title="Upcoming" count={upcoming.length} />
            {upcoming.map((appt) => <AppointmentCard key={appt.id} appt={appt} />)}
          </>
        )}

        {completed.length > 0 && (
          <>
            <SectionHeader title="Completed" count={completed.length} />
            {completed.map((appt) => <AppointmentCard key={appt.id} appt={appt} />)}
          </>
        )}

        {cancelled.length > 0 && (
          <>
            <SectionHeader title="Cancelled" count={cancelled.length} />
            {cancelled.map((appt) => <AppointmentCard key={appt.id} appt={appt} />)}
          </>
        )}

        {/* Reschedule Modal */}
        {rescheduleId && (
          <div className="modal-overlay">
            <div className="modal-card">
              <h2 className="modal-title">Reschedule Appointment</h2>

              <label className="modal-label">New Date</label>
              <DatePicker
                selected={new Date(newDate)}
                onChange={(d) => setNewDate(d.toISOString().split("T")[0])}
                minDate={new Date()}
                dateFormat="yyyy-MM-dd"
                className="modal-input"
              />

              <label className="modal-label">Duration (minutes)</label>
              <select value={newDuration} onChange={(e) => setNewDuration(e.target.value)} className="modal-input">
                <option value="15">15 min</option>
                <option value="30">30 min</option>
                <option value="45">45 min</option>
                <option value="60">60 min</option>
              </select>

              <label className="modal-label">New Time</label>
              <select value={newTime} onChange={(e) => setNewTime(e.target.value)} className="modal-input">
                <option value="">Select a slot</option>
                {availableSlots.map((slot, i) => (
                  <option key={i} value={slot.value}>{slot.label}</option>
                ))}
              </select>

              <div className="modal-footer">
                <button onClick={() => setRescheduleId(null)} className="modal-btn modal-btn-cancel">Cancel</button>
                <button onClick={rescheduleAppointment} className="modal-btn modal-btn-save">Save Changes</button>
              </div>
            </div>
          </div>
        )}
        {cancelId && (
          <div className="modal-overlay">
            <div className="modal-card">
              <h2 className="modal-title">Confirm Cancellation</h2>
              <p>Are you sure you want to cancel this appointment?</p>
              <div className="modal-footer">
                <button onClick={() => setCancelId(null)} className="modal-btn modal-btn-cancel">No</button>
                <button
                  onClick={confirmCancelAppointment}
                  className="modal-btn modal-btn-save"
                  disabled={isCancelling}
                  style={{ opacity: isCancelling ? 0.6 : 1, cursor: isCancelling ? "not-allowed" : "pointer" }}
                >
                  {isCancelling ? "Cancelling..." : "Yes, Cancel"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Appointments;